import { useEffect, useState } from "react";
import { Plus, Trash2, Package, CalendarDays, Pencil, Tag, ImagePlus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const actVacia = { nombre: "", descripcion: "", costo: "", fecha_evento: "", categoria: "general", imagen_url: "" };
const comboVacio = { nombre: "", descripcion: "", precio_total: "", actividad_ids: [], imagen_url: "" };

export default function AdminCatalogo() {
  const [actividades, setActividades] = useState(null);
  const [combos, setCombos] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [act, setAct] = useState(null);
  const [combo, setCombo] = useState(null);
  const [nuevaCat, setNuevaCat] = useState("");

  const cargar = () => {
    api.get("/actividades").then((r) => setActividades(r.data));
    api.get("/combos").then((r) => setCombos(r.data));
    api.get("/categorias", { params: { tipo: "actividad" } }).then((r) => setCategorias(r.data));
  };

  useEffect(cargar, []);

  const subirImagen = (e, setter, obj) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter({ ...obj, imagen_url: String(reader.result) });
    reader.readAsDataURL(file);
  };

  const guardarAct = async () => {
    if (!act.nombre.trim()) return toast.error("Indique el nombre de la actividad");
    if (!act.fecha_evento) return toast.error("Indique la fecha del evento");
    const payload = {
      ...act, costo: Number(act.costo || 0),
      fecha_evento: new Date(act.fecha_evento).toISOString(),
      imagen_url: act.imagen_url || null,
    };
    delete payload.id;
    delete payload.creado_en;
    try {
      if (act.id) await api.put(`/actividades/${act.id}`, payload);
      else await api.post("/actividades", payload);
      toast.success(act.id ? "Actividad actualizada" : "Actividad creada");
      setAct(null);
      cargar();
    } catch (e) { toast.error(apiError(e)); }
  };

  const guardarCombo = async () => {
    if (!combo.nombre.trim()) return toast.error("Indique el nombre del combo");
    if (!combo.precio_total || Number(combo.precio_total) <= 0) return toast.error("Indique el precio total");
    const payload = {
      nombre: combo.nombre, descripcion: combo.descripcion, precio_total: Number(combo.precio_total),
      actividad_ids: combo.actividad_ids, imagen_url: combo.imagen_url || null,
    };
    try {
      if (combo.id) await api.put(`/combos/${combo.id}`, payload);
      else await api.post("/combos", payload);
      toast.success(combo.id ? "Combo actualizado" : "Combo creado");
      setCombo(null);
      cargar();
    } catch (e) { toast.error(apiError(e)); }
  };

  const eliminar = async (recurso, id) => {
    try {
      await api.delete(`/${recurso}/${id}`);
      toast.success("Registro eliminado");
      cargar();
    } catch (e) { toast.error(apiError(e)); }
  };

  const crearCategoria = async () => {
    if (!nuevaCat.trim()) return;
    try {
      await api.post("/categorias", { nombre: nuevaCat.trim(), tipo: "actividad" });
      toast.success("Categoría agregada");
      setNuevaCat("");
      cargar();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!actividades || !combos) return <Loading />;

  return (
    <div data-testid="admin-catalogo">
      <PageTitle title="Actividades, combos y categorías"
                 subtitle="Cree, edite y ordene el catálogo con imágenes propias y sus propias categorías." />

      <Tabs defaultValue="actividades">
        <TabsList>
          <TabsTrigger value="actividades" data-testid="tab-admin-actividades">Actividades</TabsTrigger>
          <TabsTrigger value="combos" data-testid="tab-admin-combos">Combos</TabsTrigger>
          <TabsTrigger value="categorias" data-testid="tab-admin-categorias">Categorías</TabsTrigger>
        </TabsList>

        <TabsContent value="actividades" className="mt-8">
          <Button className="mb-6 rounded-full" onClick={() => setAct({ ...actVacia, categoria: categorias[0]?.nombre || "general" })}
                  data-testid="nueva-actividad">
            <Plus className="mr-2 h-4 w-4" /> Nueva actividad
          </Button>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="lista-admin-actividades">
            {actividades.map((a) => (
              <article key={a.id} className="lift overflow-hidden rounded-lg border border-border/60 bg-card">
                {a.imagen_url && <img src={a.imagen_url} alt={a.nombre} className="h-36 w-full object-cover" />}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-base font-semibold">{a.nombre}</h3>
                    <Badge variant="outline">{a.categoria}</Badge>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{a.descripcion}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                      <CalendarDays className="h-3 w-3" /> {fecha(a.fecha_evento)}
                    </span>
                    <span className="font-mono text-sm font-semibold text-primary">{money(a.costo)}</span>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full"
                            onClick={() => setAct({ ...a, costo: String(a.costo), fecha_evento: a.fecha_evento.slice(0, 10) })}
                            data-testid={`editar-actividad-${a.id}`}>
                      <Pencil className="mr-1 h-3 w-3" /> Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => eliminar("actividades", a.id)}
                            data-testid={`eliminar-actividad-${a.id}`}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="combos" className="mt-8">
          <Button className="mb-6 rounded-full" onClick={() => setCombo({ ...comboVacio })} data-testid="nuevo-combo">
            <Plus className="mr-2 h-4 w-4" /> Nuevo combo
          </Button>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="lista-admin-combos">
            {combos.map((c) => (
              <article key={c.id} className="lift overflow-hidden rounded-lg border border-border/60 bg-card">
                {c.imagen_url && <img src={c.imagen_url} alt={c.nombre} className="h-36 w-full object-cover" />}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-primary">
                    <Package className="h-4 w-4" />
                    <h3 className="font-display text-base font-bold">{c.nombre}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{c.descripcion}</p>
                  <ul className="mt-4 space-y-1 text-sm">
                    {c.actividades.map((a) => <li key={a.id} className="text-muted-foreground">• {a.nombre}</li>)}
                  </ul>
                  <p className="mt-4 font-display text-2xl font-extrabold">{money(c.precio_total)}</p>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full"
                            onClick={() => setCombo({ ...c, precio_total: String(c.precio_total) })}
                            data-testid={`editar-combo-${c.id}`}>
                      <Pencil className="mr-1 h-3 w-3" /> Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => eliminar("combos", c.id)}
                            data-testid={`eliminar-combo-${c.id}`}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="categorias" className="mt-8">
          <div className="flex max-w-md gap-3">
            <Input placeholder="Nueva categoría (ej. Música)" value={nuevaCat} data-testid="categoria-nombre"
                   onChange={(e) => setNuevaCat(e.target.value)} />
            <Button className="rounded-full" onClick={crearCategoria} data-testid="agregar-categoria">
              <Plus className="mr-2 h-4 w-4" /> Agregar
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="lista-categorias">
            {categorias.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
                <Tag className="h-3 w-3 text-primary" />
                <span className="text-sm">{c.nombre}</span>
                <button onClick={() => eliminar("categorias", c.id)} data-testid={`eliminar-categoria-${c.id}`}
                        className="text-destructive hover:opacity-70">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {categorias.length === 0 && <Empty testId="categorias-vacias">No hay categorías.</Empty>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!act} onOpenChange={(o) => !o && setAct(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-actividad">
          <DialogHeader>
            <DialogTitle className="font-display">{act?.id ? "Editar actividad" : "Nueva actividad"}</DialogTitle>
            <DialogDescription>La imagen se muestra en el catálogo del portal de padres.</DialogDescription>
          </DialogHeader>
          {act && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre</Label>
                <Input value={act.nombre} data-testid="actividad-nombre"
                       onChange={(e) => setAct({ ...act, nombre: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Descripción</Label>
                <Input value={act.descripcion || ""} data-testid="actividad-descripcion"
                       onChange={(e) => setAct({ ...act, descripcion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Costo (RD$)</Label>
                <Input type="number" value={act.costo} data-testid="actividad-costo"
                       onChange={(e) => setAct({ ...act, costo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Fecha del evento</Label>
                <Input type="date" value={act.fecha_evento} data-testid="actividad-fecha"
                       onChange={(e) => setAct({ ...act, fecha_evento: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Categoría</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={act.categoria} data-testid="actividad-categoria"
                        onChange={(e) => setAct({ ...act, categoria: e.target.value })}>
                  {categorias.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Imagen</Label>
                <Input type="file" accept="image/*" data-testid="actividad-imagen"
                       onChange={(e) => subirImagen(e, setAct, act)} />
                {act.imagen_url && (
                  <img src={act.imagen_url} alt="Vista previa" className="mt-2 h-28 w-full rounded-md object-cover" />
                )}
              </div>
              <Button className="sm:col-span-2 rounded-full py-6" onClick={guardarAct} data-testid="guardar-actividad">
                Guardar actividad
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!combo} onOpenChange={(o) => !o && setCombo(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-combo">
          <DialogHeader>
            <DialogTitle className="font-display">{combo?.id ? "Editar combo" : "Nuevo combo"}</DialogTitle>
            <DialogDescription>Agrupe actividades y servicios en un solo paquete.</DialogDescription>
          </DialogHeader>
          {combo && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={combo.nombre} data-testid="combo-nombre"
                       onChange={(e) => setCombo({ ...combo, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input value={combo.descripcion || ""} data-testid="combo-descripcion"
                       onChange={(e) => setCombo({ ...combo, descripcion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Precio total (RD$)</Label>
                <Input type="number" value={combo.precio_total} data-testid="combo-precio"
                       onChange={(e) => setCombo({ ...combo, precio_total: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Imagen</Label>
                <Input type="file" accept="image/*" data-testid="combo-imagen"
                       onChange={(e) => subirImagen(e, setCombo, combo)} />
                {combo.imagen_url && (
                  <img src={combo.imagen_url} alt="Vista previa" className="mt-2 h-28 w-full rounded-md object-cover" />
                )}
              </div>
              <div className="space-y-3">
                <Label>Actividades incluidas</Label>
                {actividades.map((a) => (
                  <label key={a.id} className="flex items-center gap-3 text-sm">
                    <Checkbox checked={combo.actividad_ids.includes(a.id)} data-testid={`combo-act-${a.id}`}
                              onCheckedChange={(v) => setCombo({
                                ...combo,
                                actividad_ids: v ? [...combo.actividad_ids, a.id]
                                  : combo.actividad_ids.filter((x) => x !== a.id),
                              })} />
                    {a.nombre} <span className="font-mono text-xs text-muted-foreground">{money(a.costo)}</span>
                  </label>
                ))}
              </div>
              <Button className="rounded-full py-6" onClick={guardarCombo} data-testid="guardar-combo">
                Guardar combo
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
